import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import MultiSelect from '@/components/ui/MultiSelect';

const steps = [
  'Upload Photos',
  'Set Location & Hours',
  'Setup Booking Calendar',
  'Preview & Publish',
];

const DUMMY_IMAGE = 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80';

const BOOKING_OPTIONS = [
  { label: 'Walk-ins', value: 'walkins' },
  { label: 'Phone Booking', value: 'phone' },
  { label: 'Email Booking', value: 'email' },
  { label: 'Advance Payment Required', value: 'advance' },
  { label: 'Cash Payments', value: 'cash' },
  { label: 'Card Payments', value: 'card' },
  { label: 'Online Booking', value: 'online' },
];

export const OperatorListingWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({
    photo: DUMMY_IMAGE,
    address: '',
    city: '',
    state: '',
    zip: '',
    opening: '',
    closing: '',
    bookingLink: '',
    bookingInstructions: '',
    bookingOptions: [],
  });

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBookingOptionsChange = (selected: string[]) => {
    setForm((prev) => ({ ...prev, bookingOptions: selected }));
  };

  const StepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="p-6 flex flex-col items-center gap-4">
            <img src={form.photo} alt="Uploaded" className="w-40 h-40 object-cover rounded shadow mb-2" />
            <div className="text-gray-500">[Mock] Photo uploaded. (File input disabled in demo)</div>
            <input type="file" disabled className="opacity-50 cursor-not-allowed" />
          </div>
        );
      case 1:
        return (
          <div className="p-6 grid grid-cols-1 gap-4">
            <input name="address" value={form.address} onChange={handleChange} placeholder="Address" className="input input-bordered" />
            <input name="city" value={form.city} onChange={handleChange} placeholder="City" className="input input-bordered" />
            <input name="state" value={form.state} onChange={handleChange} placeholder="State" className="input input-bordered" />
            <input name="zip" value={form.zip} onChange={handleChange} placeholder="Zip" className="input input-bordered" />
            <div className="flex gap-2">
              <input name="opening" value={form.opening} onChange={handleChange} placeholder="Opening Time" className="input input-bordered flex-1" />
              <input name="closing" value={form.closing} onChange={handleChange} placeholder="Closing Time" className="input input-bordered flex-1" />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="p-6 grid grid-cols-1 gap-4">
            <input name="bookingLink" value={form.bookingLink} onChange={handleChange} placeholder="Booking Link" className="input input-bordered" />
            <textarea name="bookingInstructions" value={form.bookingInstructions} onChange={handleChange} placeholder="Booking Instructions" className="input input-bordered" />
            <div>
              <label className="block font-semibold mb-2 text-sauna-navy">Booking & Payment Options</label>
              <MultiSelect
                options={BOOKING_OPTIONS}
                value={form.bookingOptions}
                onChange={handleBookingOptionsChange}
                placeholder="Select booking/payment options..."
              />
            </div>
            <div className="mt-4 text-gray-500">[Mock] Calendar preview would go here.</div>
          </div>
        );
      case 3:
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Preview</h2>
            <img src={form.photo} alt="Preview" className="w-32 h-32 object-cover rounded shadow mb-2" />
            <div className="mb-2"><strong>Address:</strong> {form.address}, {form.city}, {form.state} {form.zip}</div>
            <div className="mb-2"><strong>Hours:</strong> {form.opening} - {form.closing}</div>
            <div className="mb-2"><strong>Booking Link:</strong> {form.bookingLink}</div>
            <div className="mb-2"><strong>Instructions:</strong> {form.bookingInstructions}</div>
            <div className="mb-2"><strong>Booking & Payment Options:</strong> {form.bookingOptions.map(val => BOOKING_OPTIONS.find(opt => opt.value === val)?.label).filter(Boolean).join(', ')}</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-xl mx-auto bg-white rounded shadow p-8 mt-8">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between mb-2 text-sm font-medium text-gray-700">
          {steps.map((label, idx) => (
            <div key={label} className={idx === currentStep ? 'text-sauna-orange' : ''}>
              {label}
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-sauna-orange h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <StepContent />

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <Button
          onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
          disabled={currentStep === 0}
          variant="outline"
        >
          Back
        </Button>
        {currentStep < steps.length - 1 ? (
          <Button
            onClick={() => setCurrentStep((s) => Math.min(steps.length - 1, s + 1))}
            className="bg-sauna-orange text-white"
          >
            Next
          </Button>
        ) : (
          <Button className="bg-green-600 text-white">Publish</Button>
        )}
      </div>
    </div>
  );
}; 